import React, { useState, useCallback } from 'react'
import {
  BarChart2, Sliders, Target, CheckCircle, XCircle,
  Lightbulb, Trophy, RotateCcw, ChevronRight, Zap
} from 'lucide-react'
import FunctionGraph from '../components/FunctionGraph'

const PRESETS = [
  { label: 'y = 2x + 1',    slope: 2,   intercept: 1  },
  { label: 'y = -x + 3',    slope: -1,  intercept: 3  },
  { label: 'y = 3x (정비례)', slope: 3,  intercept: 0  },
  { label: 'y = ½x - 1',    slope: 0.5, intercept: -1 },
  { label: 'y = -2x + 4',   slope: -2,  intercept: 4  },
]

// ── 그래프 퀴즈 데이터 ──────────────────────────────────────────────────────
interface GraphQuiz {
  id: string
  title: string
  description: string
  slope: number
  intercept: number
  question: string
  questionType: 'slope' | 'intercept' | 'xIntercept' | 'quadrant'
  correctAnswer: number | string
  choices: string[]
  hint: string
  explanation: string[]
  concept: string
}

const GRAPH_QUIZZES: GraphQuiz[] = [
  {
    id: 'GQ01', title: '기울기 읽기 미션 1',
    description: '그래프를 보고 기울기를 정확히 읽어보세요!',
    slope: 2, intercept: 1,
    question: '이 그래프의 기울기(a)는 얼마인가요?',
    questionType: 'slope',
    correctAnswer: '②',
    choices: ['① 1', '② 2', '③ 3', '④ -2'],
    hint: '기울기 = Δy ÷ Δx — x가 1 증가할 때 y의 변화량을 그래프에서 읽어보세요!',
    explanation: [
      'x = 0 일 때 y = 1, x = 1 일 때 y = 3',
      'Δy = 3 − 1 = 2, Δx = 1 − 0 = 1',
      '기울기 = 2/1 = 2',
    ],
    concept: '기울기 읽기',
  },
  {
    id: 'GQ02', title: 'y절편 읽기 미션',
    description: '그래프가 y축과 만나는 점을 찾아보세요!',
    slope: -1, intercept: 3,
    question: '이 그래프의 y절편(b)은 얼마인가요?',
    questionType: 'intercept',
    correctAnswer: '③',
    choices: ['① -3', '② 1', '③ 3', '④ -1'],
    hint: 'y절편은 직선이 y축(x = 0인 선)과 만나는 점의 y좌표입니다!',
    explanation: [
      'x = 0 일 때 y의 값을 그래프에서 읽습니다',
      '직선이 y축의 (0, 3)을 지나므로 y절편 = 3',
    ],
    concept: 'y절편 읽기',
  },
  {
    id: 'GQ03', title: '기울기 방향 미션',
    description: '기울기의 부호가 그래프 모양에 어떤 영향을 주는지 확인하세요!',
    slope: -2, intercept: 4,
    question: 'y = -2x + 4 의 기울기 특성은?',
    questionType: 'slope',
    correctAnswer: '②',
    choices: ['① 양수 기울기 → 우상향', '② 음수 기울기 → 우하향', '③ 기울기 = 0 → 수평선', '④ 원점을 지남'],
    hint: '기울기 a < 0 이면 x가 증가할수록 y가 감소하는 직선입니다!',
    explanation: [
      '기울기 a = -2 < 0 (음수)',
      '음수 기울기 → 오른쪽으로 갈수록 아래로 내려감 (우하향)',
      'y절편 = 4 이므로 원점을 지나지 않음',
    ],
    concept: '기울기 방향성',
  },
  {
    id: 'GQ04', title: 'x절편 계산 미션',
    description: '그래프와 x축의 교점을 계산해 보세요!',
    slope: 0.5, intercept: -1,
    question: 'y = ½x - 1 의 x절편은?',
    questionType: 'xIntercept',
    correctAnswer: '③',
    choices: ['① -2', '② -1', '③ 2', '④ 1'],
    hint: 'x절편: y = 0 을 대입하여 x를 구합니다! → 0 = ½x - 1 → x = ?',
    explanation: [
      'x절편: y = 0 대입',
      '0 = ½x - 1',
      '½x = 1 → x = 2',
      'x절편 = 2, 점 (2, 0)에서 x축과 만남',
    ],
    concept: 'x절편 계산',
  },
  {
    id: 'GQ05', title: '정비례 함수 판별 미션',
    description: '그래프가 원점을 지나는지 확인해 보세요!',
    slope: 3, intercept: 0,
    question: 'y = 3x 의 그래프 특성으로 올바른 것은?',
    questionType: 'intercept',
    correctAnswer: '①',
    choices: ['① 원점(0, 0)을 지나는 정비례 함수', '② y절편이 3인 일차함수', '③ 기울기가 없는 수평선', '④ x절편이 3'],
    hint: 'y = ax (b = 0) 형태인 함수는 항상 원점을 지납니다!',
    explanation: [
      'y = 3x 에서 b = 0 → y절편 = 0',
      'x = 0 대입: y = 3 × 0 = 0 → 점 (0, 0) = 원점',
      '정비례 함수는 y = ax 형태, 항상 원점(0, 0)을 통과!',
    ],
    concept: '정비례 함수 · 원점',
  },
]

// ── 퀴즈 컴포넌트 ───────────────────────────────────────────────────────────
function GraphQuizPanel() {
  const [quizIdx, setQuizIdx] = useState(0)
  const [selected, setSelected] = useState<string | null>(null)
  const [showHint, setShowHint] = useState(false)
  const [showExplanation, setShowExplanation] = useState(false)
  const [scores, setScores] = useState<boolean[]>([])
  const [finished, setFinished] = useState(false)

  const quiz = GRAPH_QUIZZES[quizIdx]
  const isCorrect = selected === quiz.correctAnswer
  const answered  = selected !== null
  const totalScore = scores.filter(Boolean).length

  const handleSelect = (choice: string) => {
    if (answered) return
    setSelected(choice)
    const correct = choice === quiz.correctAnswer
    setScores(prev => [...prev, correct])
    if (correct) setShowExplanation(true)
  }

  const handleNext = () => {
    if (quizIdx < GRAPH_QUIZZES.length - 1) {
      setQuizIdx(q => q + 1)
      setSelected(null)
      setShowHint(false)
      setShowExplanation(false)
    } else {
      setFinished(true)
    }
  }

  const handleReset = () => {
    setQuizIdx(0)
    setSelected(null)
    setShowHint(false)
    setShowExplanation(false)
    setScores([])
    setFinished(false)
  }

  if (finished) {
    const pct = Math.round((totalScore / GRAPH_QUIZZES.length) * 100)
    return (
      <div className="card text-center py-8">
        <div className="text-5xl mb-3">{pct >= 80 ? '🏆' : pct >= 60 ? '🥈' : '📚'}</div>
        <h3 className="text-xl font-black text-slate-800 mb-1">그래프 퀴즈 완료!</h3>
        <p className="text-slate-500 mb-4">
          {GRAPH_QUIZZES.length}문제 중 <span className="font-black text-primary-600">{totalScore}문제 정답</span> ({pct}%)
        </p>
        <div className="flex gap-1.5 justify-center mb-5">
          {scores.map((ok, i) => (
            <span key={i} className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white ${ok ? 'bg-emerald-500' : 'bg-red-400'}`}>
              {ok ? '✓' : '✗'}
            </span>
          ))}
        </div>
        <div className={`inline-flex px-4 py-2 rounded-full text-sm font-bold mb-5 ${pct >= 80 ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
          {pct >= 80 ? '👏 그래프 읽기 실력이 훌륭합니다!' : '💡 개념 학습 탭에서 관련 단계를 복습해보세요!'}
        </div>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-primary-500 text-white rounded-xl font-bold text-sm hover:bg-primary-600 transition-colors"
        >
          <RotateCcw size={14} /> 다시 풀기
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* 퀴즈 헤더 */}
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 bg-violet-500 rounded-lg flex items-center justify-center">
            <Target size={14} className="text-white" />
          </div>
          <span className="text-sm font-bold text-violet-800">{quiz.title}</span>
          <span className="ml-auto text-xs text-violet-500 font-semibold">{quizIdx + 1} / {GRAPH_QUIZZES.length}</span>
        </div>
        <p className="text-xs text-violet-600">{quiz.description}</p>
        {/* 진행 바 */}
        <div className="mt-2 bg-violet-100 rounded-full h-1.5">
          <div
            className="h-1.5 rounded-full bg-violet-500 transition-all"
            style={{ width: `${((quizIdx + 1) / GRAPH_QUIZZES.length) * 100}%` }}
          />
        </div>
      </div>

      {/* 그래프 미리보기 */}
      <div className="card p-3">
        <p className="text-xs font-semibold text-slate-500 mb-2">📊 이 그래프를 분석하세요</p>
        <FunctionGraph slope={quiz.slope} intercept={quiz.intercept} height={200} />
        <p className="text-xs text-center text-slate-400 mt-1 font-mono">
          {quiz.intercept === 0 ? `y = ${quiz.slope}x` : quiz.intercept > 0 ? `y = ${quiz.slope}x + ${quiz.intercept}` : `y = ${quiz.slope}x ${quiz.intercept}`}
        </p>
      </div>

      {/* 질문 + 보기 */}
      <div className="card">
        <p className="text-sm font-bold text-slate-800 mb-3">{quiz.question}</p>
        <div className="space-y-2 mb-3">
          {quiz.choices.map((choice, i) => {
            const num = `${['①','②','③','④'][i]}`
            const isSelected  = selected !== null && (selected === num || choice.startsWith(num))
            const isThisRight = num === quiz.correctAnswer
            return (
              <button
                key={i}
                onClick={() => handleSelect(num)}
                className={`w-full text-left px-4 py-2.5 rounded-xl border-2 text-sm transition-all ${
                  !answered
                    ? 'border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50'
                    : isThisRight
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                      : isSelected
                        ? 'border-red-300 bg-red-50 text-red-700'
                        : 'border-slate-100 bg-slate-50 text-slate-400'
                }`}
              >
                {choice}
                {answered && isThisRight && <span className="ml-2 text-emerald-600 font-bold">✓ 정답</span>}
                {answered && isSelected && !isThisRight && <span className="ml-2 text-red-500 font-bold">✗</span>}
              </button>
            )
          })}
        </div>

        {/* 힌트 버튼 */}
        {!answered && (
          <button
            onClick={() => setShowHint(!showHint)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
          >
            <Lightbulb size={11} /> {showHint ? '힌트 숨기기' : '힌트 보기'}
          </button>
        )}

        {showHint && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
            <p className="text-xs font-bold text-amber-600 mb-1">💡 힌트</p>
            <p className="text-sm text-amber-800">{quiz.hint}</p>
          </div>
        )}

        {/* 결과 피드백 */}
        {answered && (
          <div className={`mt-3 rounded-xl border-2 p-3 ${isCorrect ? 'border-emerald-300 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex items-center gap-2 mb-2">
              {isCorrect
                ? <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                : <XCircle size={16} className="text-red-500 flex-shrink-0" />
              }
              <span className={`text-sm font-bold ${isCorrect ? 'text-emerald-700' : 'text-red-700'}`}>
                {isCorrect ? '정답입니다! 🎉' : '틀렸어요. 풀이를 확인해보세요.'}
              </span>
            </div>
            {/* 단계별 풀이 */}
            <button
              onClick={() => setShowExplanation(!showExplanation)}
              className="text-xs text-blue-600 hover:text-blue-800 underline transition-colors"
            >
              {showExplanation ? '풀이 숨기기' : '단계별 풀이 보기'}
            </button>
            {showExplanation && (
              <div className="mt-2 bg-white border border-slate-200 rounded-lg p-3">
                {quiz.explanation.map((line, i) => (
                  <div key={i} className="flex gap-2 mb-1.5">
                    <span className="w-5 h-5 bg-blue-500 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 font-bold">{i + 1}</span>
                    <p className="text-xs text-slate-700">{line}</p>
                  </div>
                ))}
                <p className="text-xs text-slate-400 mt-2 border-t pt-2">📌 개념: {quiz.concept}</p>
              </div>
            )}
          </div>
        )}

        {/* 다음 버튼 */}
        {answered && (
          <button
            onClick={handleNext}
            className="mt-3 w-full py-2.5 bg-violet-500 text-white rounded-xl font-bold text-sm hover:bg-violet-600 transition-colors flex items-center justify-center gap-2"
          >
            {quizIdx < GRAPH_QUIZZES.length - 1 ? (
              <><ChevronRight size={14} /> 다음 문제</>
            ) : (
              <><Trophy size={14} /> 결과 보기</>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

// ── 메인 페이지 ─────────────────────────────────────────────────────────────
export default function GraphPage() {
  const [slope, setSlope] = useState(2)
  const [intercept, setIntercept] = useState(1)
  const [xMin, setXMin] = useState(-5)
  const [xMax, setXMax] = useState(5)
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore')

  const equation = intercept === 0
    ? `y = ${slope}x`
    : intercept > 0
      ? `y = ${slope}x + ${intercept}`
      : `y = ${slope}x ${intercept}`

  const xIntercept = slope !== 0 ? Math.round(-intercept / slope * 100) / 100 : null

  const table = []
  for (let x = -3; x <= 3; x++) {
    table.push({ x, y: Math.round((slope * x + intercept) * 100) / 100 })
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <BarChart2 size={22} className="text-emerald-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">그래프 탐색기</h1>
            <p className="text-sm text-slate-500">기울기와 y절편을 조정하여 1차 함수 그래프를 탐색하세요</p>
          </div>
        </div>
        {/* 모드 전환 버튼 */}
        <div className="flex rounded-xl border-2 border-slate-200 overflow-hidden">
          <button
            onClick={() => setMode('explore')}
            className={`px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-1.5 ${
              mode === 'explore'
                ? 'bg-emerald-500 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Sliders size={14} /> 탐색 모드
          </button>
          <button
            onClick={() => setMode('quiz')}
            className={`px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-1.5 ${
              mode === 'quiz'
                ? 'bg-violet-500 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Target size={14} /> 퀴즈 미션
          </button>
        </div>
      </div>

      {mode === 'quiz' ? (
        <div className="max-w-2xl mx-auto">
          <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 mb-4 flex items-start gap-3">
            <Zap size={18} className="text-violet-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-violet-800 mb-1">그래프 읽기 퀴즈 미션</p>
              <p className="text-xs text-violet-600 leading-relaxed">
                그래프를 보고 기울기·y절편·x절편을 직접 읽어보는 5가지 미션입니다.
                힌트와 단계별 풀이를 참고하여 그래프 해석 실력을 키우세요!
              </p>
            </div>
          </div>
          <GraphQuizPanel />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Controls */}
          <div className="space-y-4">
            {/* Presets */}
            <div className="card">
              <p className="text-sm font-semibold text-slate-700 mb-3">함수 프리셋</p>
              <div className="space-y-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => { setSlope(p.slope); setIntercept(p.intercept) }}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm font-mono transition-all ${slope === p.slope && intercept === p.intercept ? 'bg-primary-100 text-primary-700 font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sliders */}
            <div className="card space-y-5">
              <div className="flex items-center gap-2 mb-1">
                <Sliders size={15} className="text-primary-500" />
                <p className="text-sm font-semibold text-slate-700">값 조정</p>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600">기울기 (a)</label>
                  <span className={`text-sm font-bold font-mono ${slope > 0 ? 'text-emerald-600' : slope < 0 ? 'text-red-500' : 'text-slate-500'}`}>{slope}</span>
                </div>
                <input type="range" min={-5} max={5} step={0.5} value={slope}
                  onChange={e => setSlope(Number(e.target.value))}
                  className="w-full accent-primary-500" />
                <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                  <span>-5 (급한 하강)</span><span>0</span><span>5 (급한 상승)</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600">y절편 (b)</label>
                  <span className="text-sm font-bold font-mono text-blue-600">{intercept}</span>
                </div>
                <input type="range" min={-5} max={5} step={0.5} value={intercept}
                  onChange={e => setIntercept(Number(e.target.value))}
                  className="w-full accent-blue-500" />
                <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                  <span>-5</span><span>0 (원점)</span><span>5</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">x 최솟값</label>
                  <input type="number" value={xMin} onChange={e => setXMin(Number(e.target.value))}
                    className="input w-full mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">x 최댓값</label>
                  <input type="number" value={xMax} onChange={e => setXMax(Number(e.target.value))}
                    className="input w-full mt-1 text-sm" />
                </div>
              </div>
            </div>

            {/* Info cards */}
            <div className="card space-y-2 bg-slate-50">
              <p className="text-xs font-semibold text-slate-600 mb-2">함수 정보</p>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">방정식</span>
                <span className="font-mono font-bold text-slate-800">{equation}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">기울기</span>
                <span className={`font-bold ${slope > 0 ? 'text-emerald-600' : slope < 0 ? 'text-red-500' : 'text-slate-500'}`}>{slope}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">y절편</span>
                <span className="font-bold text-blue-600">{intercept}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">x절편</span>
                <span className="font-bold text-orange-600">{xIntercept ?? '없음'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">방향</span>
                <span className="font-medium text-slate-700">
                  {slope > 0 ? '📈 증가 (우상향)' : slope < 0 ? '📉 감소 (우하향)' : '➡️ 수평선'}
                </span>
              </div>
              {intercept === 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">유형</span>
                  <span className="font-medium text-emerald-600">✓ 정비례 함수</span>
                </div>
              )}
            </div>

            {/* 퀴즈 미션 유도 버튼 */}
            <button
              onClick={() => setMode('quiz')}
              className="w-full py-3 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold text-sm flex items-center justify-center gap-2 hover:from-violet-600 hover:to-purple-700 transition-all shadow-md"
            >
              <Target size={16} /> 그래프 퀴즈 미션 도전하기 →
            </button>
          </div>

          {/* Graph + Table */}
          <div className="lg:col-span-2 space-y-4">
            <div className="card">
              <FunctionGraph slope={slope} intercept={intercept} xRange={[xMin, xMax]} height={360} />
            </div>

            {/* Value table */}
            <div className="card">
              <p className="text-sm font-semibold text-slate-700 mb-3">값 대응표</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-center">
                  <thead>
                    <tr className="bg-slate-50">
                      <td className="py-2 px-3 text-slate-500 font-semibold border-b border-slate-200">x</td>
                      {table.map(r => <td key={r.x} className="py-2 px-3 font-mono border-b border-slate-200 text-slate-700">{r.x}</td>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 px-3 text-slate-500 font-semibold">y</td>
                      {table.map(r => (
                        <td key={r.x} className={`py-2 px-3 font-mono font-semibold ${r.x === 0 ? 'text-blue-600 bg-blue-50' : 'text-primary-600'}`}>
                          {r.y}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-2 text-center">파란색 열: y절편 (x=0일 때)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
